import { ColorPicker, Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import { Form, Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useGradeOptions } from '../../../hooks/useGradeOptions';
import { useIsUsedInLesson } from '../../../hooks/useIsUsedInLesson';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { ColorType } from '../../../types/global';
import {
  use_TeachingBlocksQuery,
  useCreateSubjectContainersMutation,
  useGradeGroupsListQuery,
  useSubjectContainersListQuery,
  useUpdateSubjectContainerMutation,
} from '../../../types/planung-graphql-client-defs';
import { colorPickerOptions, UsedColors } from '../../../utils/colorPickerOptions';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { SchoolYearWithoutHolidayType } from '../../SchoolYear/graphql/types';
import { schema } from './validation/schema';

type SubjectContainerFormType = {
  uuid: string;
  name: string;
  shortName: string;
  order: number;
  grades: number[];
  gradeGroup: {
    uuid: string;
    shortName: string;
  }[];
  color: ColorType;
  validFrom: SchoolYearWithoutHolidayType | null;
  validUntil: SchoolYearWithoutHolidayType | null;
};

type SubjectContainerFormProps = {
  subjectContainerUuid: string | null;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const SubjectContainerForm = ({ subjectContainerUuid, onLoad, onClose }: SubjectContainerFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const subjectContainerContext = useMemorizedCacheTag('SUBJECT_CONTAINER');
  const gradeGroupsContext = useMemorizedCacheTag('GRADE_GROUPS');
  const teachingBlocksContext = useMemorizedCacheTag('TEACHINGBLOCK');

  const [, updateSubjectContainer] = useUpdateSubjectContainerMutation();
  const [, createSubjectContainer] = useCreateSubjectContainersMutation();

  const check = useIsUsedInLesson();

  const [{ data: teachingBlocksData }] = use_TeachingBlocksQuery({
    context: teachingBlocksContext,
  });

  const [{ data: subjectContainersData }] = useSubjectContainersListQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
    },
    context: subjectContainerContext,
  });

  const usedInTeachingBlock = subjectContainerUuid
    ? teachingBlocksData?.teachingBlocks?.findIndex((tb) => tb.subjectContainer.uuid === subjectContainerUuid) !== -1
    : false;
  const usedInLesson = subjectContainerUuid ? check(subjectContainerUuid, 'subjectContainer').isUsed : false;

  const [{ data: gradeGroupsResult }] = useGradeGroupsListQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
    },
    context: gradeGroupsContext,
  });

  const gradeGroupsOptions = useCreateSelectOptions(gradeGroupsResult?.gradeGroups, 'uuid', 'shortName');

  const classNumberOptions = useGradeOptions();
  const schoolYearOptions: SelectOptionType[] =
    subjectContainersData?.schoolYears.map((sy) => {
      return { value: sy, label: sy.name };
    }) ?? [];

  const subjectContainer =
    subjectContainersData?.subjectContainers.find((s) => s.uuid === subjectContainerUuid) ?? null;

  const initialValues: SubjectContainerFormType = {
    uuid: subjectContainer?.uuid ?? '',
    name: subjectContainer?.name ?? '',
    shortName: subjectContainer?.shortName ?? '',
    order: subjectContainer?.order ?? (subjectContainersData?.subjectContainers.length ?? 0) + 1,
    grades: subjectContainer?.grades ?? [],
    gradeGroup:
      subjectContainer?.gradeGroup.map((gg) => {
        return { uuid: gg.uuid, shortName: gg.shortName };
      }) ?? [],
    color: subjectContainer?.timetableConfig ?? {},
    validFrom: subjectContainer?.validFrom ?? currentSchoolYear,
    validUntil: subjectContainer?.validUntil ?? null,
  };

  const colorsInUse: UsedColors[] =
    subjectContainersData?.subjectContainers?.map((sc) => {
      return {
        color: sc.timetableConfig?.color,
        owner: {
          __typename: 'SubjectContainer',
          name: sc.name,
        },
      };
    }) ?? [];

  const colorPickerData = colorPickerOptions({
    htmlColorsInUse: colorsInUse,
    currentColor: initialValues.color.color ?? '',
    currentColorUsedBy: initialValues.name,
    forTypename: 'SubjectContainer',
  });

  const handleSubmit = async (values: SubjectContainerFormType, helpers: FormikHelpers<SubjectContainerFormType>) => {
    onLoad(true);

    if (subjectContainerUuid) {
      const validUntilUpdate = values.validUntil?.uuid
        ? { disconnect: {}, connect: { where: { node: { uuid: values.validUntil?.uuid } } } }
        : { disconnect: {} };

      const { error } = await updateSubjectContainer(
        {
          uuid: values.uuid,
          update: {
            gradeGroup: [
              {
                disconnect: [{}],
                connect: values.gradeGroup.map((gg) => {
                  return { where: { node: { uuid: gg.uuid } } };
                }),
              },
            ],
            grades: values.grades ?? -1,
            name: values.name,
            order: values.order,
            shortName: values.shortName,
            validFrom: { disconnect: {}, connect: { where: { node: { uuid: values.validFrom?.uuid } } } },
            validUntil: validUntilUpdate,
            timetableConfig: {
              update: {
                node: {
                  colorLabel: values.color?.colorLabel ?? '',
                  color: values.color?.color ?? '',
                },
              },
            },
          },
        },
        subjectContainerContext,
      );

      if (error) {
        showUserErrorToast({ error });
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const validUntilCreate = values.validUntil?.uuid
        ? { connect: { where: { node: { uuid: values.validUntil?.uuid } } } }
        : {};
      const { error } = await createSubjectContainer(
        {
          input: {
            gradeGroup: {
              connect: values.gradeGroup.map((gg) => {
                return { where: { node: { uuid: gg.uuid } } };
              }),
            },
            grades: values.grades ?? -1,
            name: values.name,
            shortName: values.shortName,
            order: values.order,
            timetableConfig: {
              create: {
                node: {
                  organization: {
                    connect: { where: { node: { uuid: pimAuthClaims.getOrganizationUuid() } } },
                  },
                  colorLabel: values.color?.colorLabel ?? '',
                  color: values.color?.color ?? '',
                },
              },
            },
            organization: {
              connect: { where: { node: { uuid: pimAuthClaims.getOrganizationUuid() } } },
            },
            validFrom: { connect: { where: { node: { uuid: values.validFrom?.uuid } } } },
            validUntil: validUntilCreate,
          },
        },
        subjectContainerContext,
      );

      if (error) {
        showUserErrorToast({ error });
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  };

  return (
    <Formik<SubjectContainerFormType> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
      {({ values, handleChange, handleBlur, resetForm, touched, errors, setFieldTouched, setFieldValue }) => (
        <Form>
          <Grid useFormGap>
            <GridRow spacingBottom='s'>
              <GridColumn width={9}>
                <Input
                  label={t('common.name')}
                  name='name'
                  onChange={handleChange}
                  value={values.name}
                  error={touched.name ? errors.name : undefined}
                />
              </GridColumn>
              <GridColumn width={3}>
                <ColorPicker
                  name='color'
                  label={t('common.color')}
                  options={colorPickerData.groupedColorOptions}
                  maxMenuHeight={300}
                  tooltipText=''
                  onChange={(option) => {
                    setFieldValue('color.color', option?.html ?? '');
                    setFieldValue('color.colorLabel', option?.label ?? '');
                    setFieldTouched('color', true, false);
                  }}
                  onBlur={handleBlur}
                  defaultValue={
                    values.color
                      ? {
                          value: values.color.color ?? '',
                          html: values.color.color ?? '',
                          label: values.color.colorLabel ?? '',
                        }
                      : undefined
                  }
                />
              </GridColumn>
            </GridRow>
            <GridRow spacingBottom='s' spacingTop='s'>
              <GridColumn width={3}>
                <Input
                  label={t('subjectContainer.shortName')}
                  name='shortName'
                  onChange={handleChange}
                  value={values.shortName}
                  error={touched.shortName ? errors.shortName : undefined}
                />
              </GridColumn>
              <GridColumn width={3}>
                <Input
                  label={t('subjectContainer.order')}
                  name='order'
                  onChange={handleChange}
                  type='number'
                  value={values.order ?? 0}
                  error={touched.order ? errors.order : undefined}
                />
              </GridColumn>
              <GridColumn width={3}>
                <Select
                  label={t('subjectContainer.validFrom')}
                  name='validFrom'
                  options={schoolYearOptions}
                  onChange={(option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    setFieldTouched('validFrom', true);
                    setFieldValue('validFrom', opt?.value, true);
                  }}
                  value={values.validFrom ? { value: values.validFrom, label: values.validFrom.name } : null}
                  error={errors.validFrom}
                />
              </GridColumn>
              <GridColumn width={3}>
                <Select
                  label={t('subjectContainer.validUntil')}
                  isClearable
                  name='validUntil'
                  options={schoolYearOptions}
                  onChange={(option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    setFieldTouched('validUntil', true);
                    setFieldValue('validUntil', opt?.value, true);
                  }}
                  value={values.validUntil ? { value: values.validUntil, label: values.validUntil.name } : null}
                />
              </GridColumn>
            </GridRow>
            <GridRow spacingBottom='s' spacingTop='s'>
              <GridColumn width={6}>
                <Select
                  label={t('grades.title', { count: 2 })}
                  isMulti
                  isClearable
                  isSearchable
                  tooltipText={t('subjectContainer.validForCombinations')}
                  name='grades'
                  menuPosition='fixed'
                  options={classNumberOptions}
                  onChange={async (_option) => {
                    // if usedInLesson || usedInTeachingBlock we only allow add not remove
                    if (usedInLesson || usedInTeachingBlock) {
                      const options = _option as MultiValue<SelectOptionType>;
                      const newValues = options.map((o) => o.value as number);
                      const filteredValues = newValues.filter((grade) => !subjectContainer?.grades.includes(grade));
                      await setFieldValue(
                        'grades',
                        subjectContainer ? [...subjectContainer.grades, ...filteredValues] : filteredValues,
                      );
                      return;
                    }

                    const options = _option as MultiValue<SelectOptionType>;
                    await setFieldValue(
                      'grades',
                      options.map((o) => o.value),
                    );
                  }}
                  value={values.grades.map((grade) => {
                    return { value: grade, label: `${grade}` };
                  })}
                />
              </GridColumn>
              <GridColumn width={6}>
                <Select
                  label={t('gradeGroups.title', { count: 2 })}
                  isMulti
                  isClearable
                  isSearchable
                  tooltipText={t('subjectContainer.validForCombinations')}
                  name='gradeGroup'
                  menuPosition='fixed'
                  options={gradeGroupsOptions}
                  onChange={async (_option) => {
                    //if usedInLesson || usedInTeachingBlock we only allow add not remove
                    if (usedInLesson || usedInTeachingBlock) {
                      const options = _option as MultiValue<SelectOptionType>;
                      const filteredValues = options.filter(
                        (option) => !subjectContainer?.gradeGroup.some((g) => g.uuid === option.value),
                      );
                      await setFieldValue(
                        'gradeGroup',
                        subjectContainer
                          ? [
                              ...subjectContainer.gradeGroup.map((gg) => ({ uuid: gg.uuid, shortName: gg.shortName })),
                              ...filteredValues.map((option) => {
                                return { uuid: option.value, shortName: option.label };
                              }),
                            ]
                          : filteredValues,
                      );
                      return;
                    }

                    const options = _option as MultiValue<SelectOptionType>;
                    const uuids = options.map((o) => o.value);
                    const data = gradeGroupsResult?.gradeGroups.filter((gg) => {
                      return uuids.includes(gg.uuid);
                    });
                    await setFieldValue('gradeGroup', data);
                  }}
                  value={values.gradeGroup.map((gg) => {
                    return { value: gg.uuid, label: gg.shortName };
                  })}
                />
              </GridColumn>
            </GridRow>
            {(usedInLesson || usedInTeachingBlock) && (
              <GridRow spacingTop='none' spacingBottom='none'>
                <GridColumn width={12}>
                  <div style={{ fontSize: 'var(--font-size-s)' }}>
                    {t('subjectContainer.usedSubjectContainerEditText')}
                  </div>
                </GridColumn>
              </GridRow>
            )}
          </Grid>

          <ModalBottomButtons
            closeButton={{
              callback: () => {
                resetForm();
                onClose();
              },
            }}
          />
        </Form>
      )}
    </Formik>
  );
};
